import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pagination, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { HiMagnifyingGlass, HiAcademicCap, HiXMark } from 'react-icons/hi2';
import { PageContent, PageHeader } from '@/components/layout/PageLayout';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { DataTable, type DataTableColumn } from '@/components/common/DataTable';
import {
  EmptyState, EmptyStateIcon, EmptyStateTitle, EmptyStateDescription,
  DesktopOnly, MobileOnly, MobileCardList, MobileCard, MobileCardHeader, MobileCardBody, MobileCardMain, MobileCardTitle,
  TextMutedSmall,
} from '@/styles/shared.styled';
import {
  useSearchLegalProfessionalsQuery,
  useGetLegalProfessionalFilterOptionsQuery,
} from '@/store/legalProfessionalsApi';
import {
  TypeSwitchContainer,
  TypeSwitchButton,
  FiltersRow,
  FilterGroup,
  FilterLabel,
  ResultCount,
  StatusBadge,
  ClearFiltersButton,
  SearchInputWrapper,
} from './LegalProfessionalDirectoryPage.styled';
import type { LegalProfessionalListItemDto } from '@/types';

const PAGE_SIZE = 20;

const PROFESSIONAL_TYPES = [
  { value: 'Adwokat', labelKey: 'legalDirectory.types.adwokat' },
  { value: 'AplikantAdwokacki', labelKey: 'legalDirectory.types.aplikantAdwokacki' },
  { value: 'RadcaPrawny', labelKey: 'legalDirectory.types.radcaPrawny' },
  { value: 'AplikantRadcowski', labelKey: 'legalDirectory.types.aplikantRadcowski' },
];

function useDebounce(value: string, delay: number): string {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

export default function LegalProfessionalDirectoryPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [selectedType, setSelectedType] = useState<string>('Adwokat');
  const [searchInput, setSearchInput] = useState('');
  const [registryNumber, setRegistryNumber] = useState('');
  const [status, setStatus] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [barAssociationId, setBarAssociationId] = useState<string>('');
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounce(searchInput, 300);
  const debouncedRegistryNumber = useDebounce(registryNumber, 300);

  const { data: filterOptions } = useGetLegalProfessionalFilterOptionsQuery();

  const { data, isLoading, isFetching } = useSearchLegalProfessionalsQuery({
    search: debouncedSearch || undefined,
    registryNumber: debouncedRegistryNumber || undefined,
    type: selectedType || undefined,
    status: status || undefined,
    barAssociationId: barAssociationId || undefined,
    city: city || undefined,
    page,
    pageSize: PAGE_SIZE,
  });

  const totalPages = data ? Math.ceil(data.totalCount / PAGE_SIZE) : 0;

  const handleTypeChange = useCallback((type: string) => {
    setSelectedType(type);
    setBarAssociationId('');
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setPage(1);
  }, []);

  const hasActiveFilters = searchInput || registryNumber || status || city || barAssociationId;

  const clearFilters = useCallback(() => {
    setSearchInput('');
    setRegistryNumber('');
    setStatus('');
    setCity('');
    setBarAssociationId('');
    setPage(1);
  }, []);

  const filteredBarAssociations = useMemo(() => {
    const all = filterOptions?.barAssociations ?? [];
    const isAdwokat = selectedType === 'Adwokat' || selectedType === 'AplikantAdwokacki';
    const isRadca = selectedType === 'RadcaPrawny' || selectedType === 'AplikantRadcowski';
    if (!isAdwokat && !isRadca) return all;
    return all.filter((ba) => {
      if (ba.type) {
        return isAdwokat ? ba.type === 'IzbaAdwokacka' : ba.type === 'OIRP';
      }
      const name = ba.fullName.toUpperCase();
      return isAdwokat ? name.includes('ADWOKAC') : name.includes('OIRP');
    });
  }, [filterOptions?.barAssociations, selectedType]);

  const cityOptions = useMemo(
    () => (filterOptions?.cities ?? []).map((c) => ({ value: c, label: c })),
    [filterOptions?.cities],
  );

  const renderPaginationItems = () => {
    const items = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);
    start = Math.max(1, end - maxVisible + 1);

    if (start > 1) {
      items.push(<Pagination.Item key={1} onClick={() => setPage(1)}>1</Pagination.Item>);
      if (start > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
    }

    for (let i = start; i <= end; i++) {
      items.push(
        <Pagination.Item key={i} active={page === i} onClick={() => setPage(i)}>
          {i}
        </Pagination.Item>,
      );
    }

    if (end < totalPages) {
      if (end < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
      items.push(<Pagination.Item key={totalPages} onClick={() => setPage(totalPages)}>{totalPages}</Pagination.Item>);
    }

    return items;
  };

  const columns: DataTableColumn<LegalProfessionalListItemDto>[] = [
    { key: 'name', header: t('legalDirectory.table.name'), render: (p) => <strong>{p.fullName}</strong> },
    { key: 'registryNumber', header: t('legalDirectory.table.registryNumber'), render: (p) => p.registryNumber },
    { key: 'city', header: t('legalDirectory.table.city'), render: (p) => p.city ?? '—' },
    {
      key: 'status',
      header: t('legalDirectory.table.status'),
      render: (p) =>
        p.status ? (
          <StatusBadge $status={p.status}>
            {t(`legalDirectory.statuses.${p.status.toLowerCase()}`)}
          </StatusBadge>
        ) : (
          '—'
        ),
    },
    { key: 'barAssociation', header: t('legalDirectory.table.barAssociation'), render: (p) => p.barAssociationName ?? '—' },
  ];

  return (
    <PageContent $wide>
      <PageHeader title={t('legalDirectory.title')} subtitle={t('legalDirectory.subtitle')} />

      <TypeSwitchContainer>
        {PROFESSIONAL_TYPES.map(({ value, labelKey }) => (
          <TypeSwitchButton
            key={value}
            $active={selectedType === value}
            onClick={() => handleTypeChange(value)}
          >
            {t(labelKey)}
          </TypeSwitchButton>
        ))}
      </TypeSwitchContainer>

      <FiltersRow>
        <FilterGroup $wide>
          <FilterLabel>{t('legalDirectory.filters.search')}</FilterLabel>
          <SearchInputWrapper>
            <Form.Control
              type="text"
              placeholder={t('legalDirectory.filters.searchPlaceholder')}
              value={searchInput}
              onChange={handleSearchChange}
            />
            <HiMagnifyingGlass />
          </SearchInputWrapper>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>{t('legalDirectory.filters.registryNumber')}</FilterLabel>
          <Form.Control
            type="text"
            placeholder={t('legalDirectory.filters.registryNumberPlaceholder')}
            value={registryNumber}
            onChange={(e) => { setRegistryNumber(e.target.value); setPage(1); }}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>{t('legalDirectory.filters.status')}</FilterLabel>
          <Form.Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="">{t('legalDirectory.filters.allStatuses')}</option>
            <option value="Active">{t('legalDirectory.statuses.active')}</option>
            <option value="Inactive">{t('legalDirectory.statuses.inactive')}</option>
            <option value="Former">{t('legalDirectory.statuses.former')}</option>
          </Form.Select>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>{t('legalDirectory.filters.city')}</FilterLabel>
          <Select
            options={cityOptions}
            value={city ? { value: city, label: city } : null}
            onChange={(option) => { setCity(option?.value ?? ''); setPage(1); }}
            isClearable
            placeholder={t('legalDirectory.filters.allCities')}
            noOptionsMessage={() => t('legalDirectory.filters.noCitiesFound')}
            styles={{
              control: (base) => ({ ...base, minHeight: 38, fontSize: '0.875rem' }),
              menu: (base) => ({ ...base, fontSize: '0.875rem' }),
            }}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>{t('legalDirectory.filters.barAssociation')}</FilterLabel>
          <Form.Select value={barAssociationId} onChange={(e) => { setBarAssociationId(e.target.value); setPage(1); }}>
            <option value="">{t('legalDirectory.filters.allBarAssociations')}</option>
            {filteredBarAssociations.map((ba) => (
              <option key={ba.id} value={ba.id}>{ba.fullName}</option>
            ))}
          </Form.Select>
        </FilterGroup>

        {hasActiveFilters && (
          <ClearFiltersButton type="button" onClick={clearFilters}>
            <HiXMark />
            {t('legalDirectory.filters.clear')}
          </ClearFiltersButton>
        )}
      </FiltersRow>

      {data && (
        <ResultCount>
          {t('legalDirectory.resultCount', { count: data.totalCount })}
          {isFetching && !isLoading && <span className="ms-2 spinner-border spinner-border-sm" />}
        </ResultCount>
      )}

      {isLoading ? (
        <LoadingSpinner />
      ) : !data?.items.length ? (
        <EmptyState>
          <EmptyStateIcon><HiAcademicCap /></EmptyStateIcon>
          <EmptyStateTitle>{t('legalDirectory.empty.title')}</EmptyStateTitle>
          <EmptyStateDescription>{t('legalDirectory.empty.description')}</EmptyStateDescription>
        </EmptyState>
      ) : (
        <>
          <DesktopOnly>
            <DataTable<LegalProfessionalListItemDto>
              ariaLabel={t('legalDirectory.title')}
              rowKey={(p) => p.id}
              onRowClick={(p) => navigate(`/legal-professionals/${p.id}`)}
              columns={columns}
              rows={data.items}
            />
          </DesktopOnly>

          <MobileOnly>
            <MobileCardList>
              {data.items.map((p) => (
                <MobileCard
                  key={p.id}
                  $clickable
                  onClick={() => navigate(`/legal-professionals/${p.id}`)}
                >
                  <MobileCardHeader>
                    <MobileCardMain>
                      <MobileCardTitle>{p.fullName}</MobileCardTitle>
                      <TextMutedSmall>{p.registryNumber}</TextMutedSmall>
                    </MobileCardMain>
                    {p.status && <StatusBadge $status={p.status}>{t(`legalDirectory.statuses.${p.status.toLowerCase()}`)}</StatusBadge>}
                  </MobileCardHeader>
                  <MobileCardBody>
                    {p.city && <span>{p.city}</span>}
                    {p.barAssociationName && <TextMutedSmall as="span">{p.barAssociationName}</TextMutedSmall>}
                  </MobileCardBody>
                </MobileCard>
              ))}
            </MobileCardList>
          </MobileOnly>

          {totalPages > 1 && (
            <div className="d-flex justify-content-end mt-3">
              <Pagination className="mb-0">
                <Pagination.Prev disabled={page <= 1} onClick={() => setPage(page - 1)} />
                {renderPaginationItems()}
                <Pagination.Next disabled={page >= totalPages} onClick={() => setPage(page + 1)} />
              </Pagination>
            </div>
          )}
        </>
      )}
    </PageContent>
  );
}
